import { useMemo } from 'react'
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme
} from '@mui/material'
import { C100Entry, C100Row } from '../../../_types/carnivore100.type'
import { c100Tokens, scrollbarSx } from '../c100-tokens'
import { FROZEN_COLUMNS } from '../columns'
import { CELL_RENDERERS } from '../row-cells'
import { LIST_NAME } from '../list-name'

const MONO = "'IBM Plex Mono', monospace"

interface Props {
  entries: C100Entry[]
  onTicker: (ticker: string) => void
}

// A published list exactly as it went out: rank order, publish-time signals,
// no quotes. Fixed column set — the live table's layout prefs are about
// reading prices, which this view doesn't have.
export default function FrozenTable({ entries, onTicker }: Props) {
  const theme = useTheme()
  const t = c100Tokens(theme)

  const sorted = useMemo(() => [...entries].sort((a, b) => a.rank - b.rank), [entries])

  const pinLefts = useMemo(() => {
    const out: Record<string, number> = {}
    let x = 0
    for (const col of FROZEN_COLUMNS) {
      if (!col.locked) break
      out[col.id] = x
      x += col.width
    }
    return out
  }, [])
  const totalWidth = FROZEN_COLUMNS.reduce((sum, c) => sum + c.width, 0)

  const cellSx = (id: string, width: number) => {
    const pinned = pinLefts[id] !== undefined
    return {
      '&&': {
        boxSizing: 'border-box',
        width,
        minWidth: width,
        maxWidth: width,
        ...(pinned
          ? { position: 'sticky', left: pinLefts[id], zIndex: 5, bgcolor: t.card, whiteSpace: 'nowrap' }
          : {})
      }
    } as const
  }

  return (
    <TableContainer
      component={Box}
      sx={{
        bgcolor: t.card,
        border: `1px solid ${t.hairline}`,
        borderRadius: 2,
        maxHeight: 800,
        overflowX: 'scroll',
        overflowY: 'auto',
        ...scrollbarSx(t, { persistent: true })
      }}
    >
      <Table
        stickyHeader
        size="small"
        aria-label={`${LIST_NAME} as published`}
        sx={{ tableLayout: 'fixed', width: totalWidth, minWidth: totalWidth }}
      >
        <TableHead>
          <TableRow>
            {FROZEN_COLUMNS.map(col => (
              <TableCell
                key={col.id}
                sx={{
                  ...cellSx(col.id, col.width),
                  bgcolor: t.card,
                  borderBottom: `1px solid ${t.hairline}`,
                  zIndex: pinLefts[col.id] !== undefined ? 30 : 20
                }}
              >
                <Tooltip title={col.help ?? ''} placement="top" arrow disableInteractive>
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: MONO,
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: t.muted
                    }}
                  >
                    {col.label}
                  </Typography>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(entry => (
            <TableRow
              key={entry.ticker}
              onClick={() => onTicker(entry.ticker)}
              sx={{
                height: 52,
                cursor: 'pointer',
                bgcolor: t.card,
                '&:last-child td': { borderBottom: 'none' },
                '&:hover': {
                  bgcolor: t.faintAccentBg,
                  boxShadow: `inset 3px 0 0 ${t.accent}`,
                  '& .c33-pinned': {
                    backgroundImage: `linear-gradient(${t.faintAccentBg}, ${t.faintAccentBg})`
                  }
                }
              }}
            >
              {FROZEN_COLUMNS.map(col => (
                <TableCell
                  key={col.id}
                  className={pinLefts[col.id] !== undefined ? 'c33-pinned' : undefined}
                  sx={{
                    ...cellSx(col.id, col.width),
                    borderBottom: `1px solid ${t.hairline}`,
                    py: 1,
                    overflow: 'hidden'
                  }}
                >
                  {/* FROZEN_COLUMNS excludes every renderer that reads a live
                      quote, so an entry satisfies what these cells touch */}
                  {CELL_RENDERERS[col.id]?.(entry as C100Row, t)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
